'use client';

import React from 'react';
import Link from '@/components/atoms/link';
import { classnames } from '@/utils/classnames/classnames';
import { CategoryPillProps } from './types';

// EWM-832 — the one definition of the category pill/chip.
//
// Extracted verbatim from the CategoryCardsGrid `subCategories` chip (EWM-264)
// so the homepage cards and the PLP subcategory nav cannot drift apart. Two
// treatments only: the default sits on a white card, `isHighlighted` on the
// filled-navy one. Phase 1 is imagery-free — icons are EWM-974.
//
// Protocol hrefs (and an explicit `openInNewTab`) bypass the localized Link
// atom and render a raw anchor, because next-intl has no locale prefix to add
// to an off-site target.
const isExternal = (href?: string) => !!href && /^(https?:|mailto:|tel:)/i.test(href);

const CategoryPill = ({ label, href, openInNewTab = false, isHighlighted = false, onClick }: CategoryPillProps) => {
  const resolvedHref = href || '#';
  const external = isExternal(resolvedHref) || openInNewTab;

  const className = classnames(
    'text-12 inline-block rounded-full border px-3 py-1.5 transition-colors focus-visible:ring-2 focus-visible:outline-none',
    isHighlighted
      ? 'hover:text-primary border-white text-white hover:bg-white focus-visible:ring-white'
      : 'hover:border-primary hover:text-primary focus-visible:ring-primary border-gray-300 text-gray-700',
  );

  if (external) {
    return (
      <a href={resolvedHref} target="_blank" rel="noopener noreferrer" onClick={onClick} className={className}>
        {label}
      </a>
    );
  }

  return (
    <Link href={resolvedHref} onClick={onClick} underlineOnHover={false} className={className}>
      {label}
    </Link>
  );
};

export default CategoryPill;
